import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { Hits, Paginated } from "../../common/types/Paginated";
import {
  addLongOrder,
  addOrder,
  Event,
  listOrderRows,
  Order,
  OrderRow,
  OrderRowDisplay,
  OrderRowFilters,
} from "./orderAPI";
import { cancelRowBundle } from "./orderRowsDetailedSlice";
import { OrderPreparedPayload } from "./orderTypes";

export type SliceState = {
  ids: [];
  entities: {};
  cursor: string | null;
  hits?: Hits;
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

// NOTE: sorting must be exactly the same as in backend
const orderRowsSortComparer = (
  a: OrderRowDisplay,
  b: OrderRowDisplay,
): number => {
  // 1) isFillShift desc (true first)
  if (a.isFillShift !== b.isFillShift) {
    return a.isFillShift ? -1 : 1;
  }
  // 2) workStarts asc (missing values last)
  const aw = a.workStarts ?? Number.MAX_SAFE_INTEGER;
  const bw = b.workStarts ?? Number.MAX_SAFE_INTEGER;
  if (aw !== bw) {
    return aw < bw ? -1 : 1;
  }
  // 3) id asc as a stable tie-breaker
  if (a.id !== b.id) {
    return a.id < b.id ? -1 : 1;
  }
  return 0;
};

const orderRowsAdapter = createEntityAdapter<OrderRowDisplay>({
  sortComparer: orderRowsSortComparer,
});

export const fetchOrderRows = createAsyncThunk<
  Paginated<OrderRowDisplay[]>,
  OrderRowFilters,
  { state: RootState }
>(
  "orderRows/fetchOrderRows",
  async (filters: OrderRowFilters, { requestId }) => {
    const response = await listOrderRows(filters, {
      requestId,
    });
    return response.data;
  },
);

type AddOrderArgs = {
  order: OrderPreparedPayload;
  idempotencyKey?: string;
};

export const addNewOrder = createAsyncThunk<
  Event<Order | OrderRow>[],
  AddOrderArgs,
  { state: RootState }
>(
  "orderRows/addOrder",
  async ({ order, idempotencyKey }, { requestId }) =>
    await addOrder(order, { requestId, idempotencyKey }),
);

export const addNewLongOrder = createAsyncThunk<
  Event<Order | OrderRow>[],
  AddOrderArgs,
  { state: RootState }
>(
  "orderRows/addLongOrder",
  async ({ order, idempotencyKey }, { requestId }) =>
    await addLongOrder(order, { requestId, idempotencyKey }),
);

export const orderRowsSlice = createSlice({
  name: "orderRows",
  initialState: orderRowsAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrderRows.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchOrderRows.fulfilled, (state, action) => {
        const orderRows = action.payload.data.map((orderRow) => {
          return {
            ...orderRow,
            id: orderRow.bundleId,
          };
        });

        // If cursor is null/undefined, it's a new search - replace all rows
        // If cursor exists, it's pagination - add/update rows
        if (!action.meta.arg.cursor) {
          orderRowsAdapter.setAll(state, orderRows);
        } else {
          orderRowsAdapter.upsertMany(state, orderRows);
        }

        state.cursor = action.payload.cursor;
        state.hits = action.payload.hits;
        state.status = "succeeded";
      })
      .addCase(fetchOrderRows.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(addNewOrder.pending, (state) => {
        state.status = "pending";
      })
      .addCase(addNewOrder.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(addNewOrder.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(addNewLongOrder.pending, (state) => {
        state.status = "pending";
      })
      .addCase(addNewLongOrder.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(addNewLongOrder.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(cancelRowBundle.pending, (state) => {
        state.status = "pending";
      })
      .addCase(cancelRowBundle.fulfilled, (state, action) => {
        orderRowsAdapter.removeOne(state, action.meta.arg.bundleId);
        state.status = "succeeded";
      })
      .addCase(cancelRowBundle.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const {
  selectAll: selectAllOrderRows,
  selectById: selectOrderById,
  selectIds: selectOrderRowIds,
} = orderRowsAdapter.getSelectors((state: RootState) => state.orderRows);

export const orderRowsReducer = orderRowsSlice.reducer;
